import { SOURCE_TYPE } from '@/types/feedbackConst';
import { ConstMap } from '@/utils/ConstMap';

import { SCRAPER_CATEGORY, SCRAPER_NAME } from '../types';
import {
  type FieldProperty,
  SCRAPER_REQ_FIELD_KEY,
} from './scraper-field-map/field-const';
import {
  CommerceReviewFieldList,
  CommunityFieldList,
  KeywordDefinitionFieldList,
  SocialMediaFieldList,
} from './scraper-field-map/scraper-base-field-list';

export type FieldPropertyOverride = Partial<FieldProperty>;

export type FieldOverrideConfig = Map<
  SCRAPER_REQ_FIELD_KEY,
  FieldPropertyOverride
>;

export type ScraperConfigs = {
  source: SOURCE_TYPE;
  category: SCRAPER_CATEGORY;
  fields: SCRAPER_REQ_FIELD_KEY[];
  /**
   * @description
   * maxItems는 Scraper가 한번에 수집할 수 있는 최대 개수 입니다.
   * Apify처럼 외부에 종속된 Scraper에 영향을 받습니다.
   */
  maxItems?: number;
  customFieldConfig?: FieldOverrideConfig;
};

export const scraperConfigMap = new ConstMap<SCRAPER_NAME, ScraperConfigs>([
  [
    SCRAPER_NAME.AmazonReviewByUrl,
    {
      source: SOURCE_TYPE.AMAZON,
      category: SCRAPER_CATEGORY.COMMERCE,
      fields: [...CommerceReviewFieldList, SCRAPER_REQ_FIELD_KEY.ALL_OPTIONS],
      maxItems: 500,
    },
  ],
  [
    SCRAPER_NAME.CoupangReview,
    {
      source: SOURCE_TYPE.COUPANG,
      category: SCRAPER_CATEGORY.COMMERCE,
      fields: [...CommerceReviewFieldList],
      maxItems: 1500,
    },
  ],
  [
    SCRAPER_NAME.DcinsidePost,
    {
      source: SOURCE_TYPE.DC_INSIDE,
      category: SCRAPER_CATEGORY.COMMUNITY,
      fields: [...CommunityFieldList, ...KeywordDefinitionFieldList],
      maxItems: 30000,
    },
  ],
  [
    SCRAPER_NAME.FacebookCommentByKeyword,
    {
      source: SOURCE_TYPE.FACEBOOK,
      category: SCRAPER_CATEGORY.SOCIAL,
      fields: [...KeywordDefinitionFieldList, ...SocialMediaFieldList],
      maxItems: 100,
    },
  ],
  [
    SCRAPER_NAME.FacebookPrivateGroup,
    {
      source: SOURCE_TYPE.FACEBOOK,
      category: SCRAPER_CATEGORY.SOCIAL,
      fields: [SCRAPER_REQ_FIELD_KEY.GROUP_ALIAS],
      maxItems: 1000,
    },
  ],
  [
    SCRAPER_NAME.FacebookCommentByUrl,
    {
      source: SOURCE_TYPE.FACEBOOK,
      category: SCRAPER_CATEGORY.SOCIAL,
      fields: [SCRAPER_REQ_FIELD_KEY.PAGE_URL, ...SocialMediaFieldList],
      maxItems: 30000,
    },
  ],
  [
    SCRAPER_NAME.FmkoreaPost,
    {
      source: SOURCE_TYPE.FMKOREA,
      category: SCRAPER_CATEGORY.COMMUNITY,
      fields: [...CommunityFieldList, ...KeywordDefinitionFieldList],
      maxItems: 30000,
    },
  ],
  [
    SCRAPER_NAME.GoogleNews,
    {
      source: SOURCE_TYPE.GOOGLE_NEWS,
      category: SCRAPER_CATEGORY.COMMUNITY,
      fields: [...KeywordDefinitionFieldList],
      maxItems: 30000,
    },
  ],
  [
    SCRAPER_NAME.InstagramCommentByKeyword,
    {
      source: SOURCE_TYPE.INSTAGRAM,
      category: SCRAPER_CATEGORY.SOCIAL,
      fields: [...KeywordDefinitionFieldList, ...SocialMediaFieldList],
      maxItems: 100,
    },
  ],
  [
    SCRAPER_NAME.InstagramCommentByProfile,
    {
      source: SOURCE_TYPE.INSTAGRAM,
      category: SCRAPER_CATEGORY.SOCIAL,
      fields: [SCRAPER_REQ_FIELD_KEY.USERNAME, ...SocialMediaFieldList],
      maxItems: 100,
    },
  ],
  [
    SCRAPER_NAME.NaverBlogPost,
    {
      source: SOURCE_TYPE.NAVER_BLOG,
      category: SCRAPER_CATEGORY.COMMUNITY,
      fields: [...CommunityFieldList, SCRAPER_REQ_FIELD_KEY.KEYWORD],
      maxItems: 30000,
    },
  ],
  [
    SCRAPER_NAME.NaverCafeAllSearch,
    {
      source: SOURCE_TYPE.NAVER_CAFE,
      category: SCRAPER_CATEGORY.COMMUNITY,
      fields: [
        ...CommunityFieldList,
        SCRAPER_REQ_FIELD_KEY.KEYWORD,
        SCRAPER_REQ_FIELD_KEY.INCLUDE_KEYWORDS,
        SCRAPER_REQ_FIELD_KEY.EXCLUDE_KEYWORDS,
        SCRAPER_REQ_FIELD_KEY.SORT,
      ],
      maxItems: 1020,
    },
  ],
  [
    SCRAPER_NAME.NaverSpecificCafeSearch,
    {
      source: SOURCE_TYPE.NAVER_CAFE,
      category: SCRAPER_CATEGORY.COMMUNITY,
      fields: [
        ...CommunityFieldList,
        SCRAPER_REQ_FIELD_KEY.KEYWORD,
        SCRAPER_REQ_FIELD_KEY.INCLUDE_KEYWORDS,
        SCRAPER_REQ_FIELD_KEY.EXCLUDE_KEYWORDS,
        SCRAPER_REQ_FIELD_KEY.CAFE_ID,
        SCRAPER_REQ_FIELD_KEY.SORT,
      ],
      maxItems: 1020,
    },
  ],
  [
    SCRAPER_NAME.NaverCafeAccountBase,
    {
      source: SOURCE_TYPE.NAVER_CAFE,
      category: SCRAPER_CATEGORY.COMMUNITY,
      fields: [
        ...CommunityFieldList,
        SCRAPER_REQ_FIELD_KEY.CAFE_ID,
        SCRAPER_REQ_FIELD_KEY.KEYWORD,
      ],
      maxItems: 30000,
      customFieldConfig: new Map([
        [
          SCRAPER_REQ_FIELD_KEY.KEYWORD,
          {
            required: false,
          },
        ],
      ]),
    },
  ],
  [
    SCRAPER_NAME.OliveyoungReview,
    {
      source: SOURCE_TYPE.OLIVEYOUNG,
      category: SCRAPER_CATEGORY.COMMERCE,
      fields: [...CommerceReviewFieldList],
      maxItems: 10000,
    },
  ],
  [
    SCRAPER_NAME.PpomppuPost,
    {
      source: SOURCE_TYPE.PPOMPPU,
      category: SCRAPER_CATEGORY.COMMUNITY,
      fields: [...CommunityFieldList, ...KeywordDefinitionFieldList],
      maxItems: 30000,
    },
  ],
  [
    SCRAPER_NAME.Qoo10Review,
    {
      source: SOURCE_TYPE.QOO10,
      category: SCRAPER_CATEGORY.COMMERCE,
      fields: [...CommerceReviewFieldList],
      maxItems: 1000,
    },
  ],
  [
    SCRAPER_NAME.RedditPostByKeyword,
    {
      source: SOURCE_TYPE.SCRAPED_REDDIT,
      category: SCRAPER_CATEGORY.COMMUNITY,
      fields: [
        ...CommunityFieldList,
        ...KeywordDefinitionFieldList,
        SCRAPER_REQ_FIELD_KEY.SUBREDDIT,
      ],
      maxItems: 100,
    },
  ],
  [
    SCRAPER_NAME.SephoraReview,
    {
      source: SOURCE_TYPE.SEPHORA,
      category: SCRAPER_CATEGORY.COMMERCE,
      fields: [...CommerceReviewFieldList],
      maxItems: 30000,
    },
  ],
  [
    SCRAPER_NAME.TiktokPostByKeywordForSyncly,
    {
      source: SOURCE_TYPE.TIKTOK,
      category: SCRAPER_CATEGORY.SOCIAL,
      fields: [...KeywordDefinitionFieldList, ...SocialMediaFieldList],
      maxItems: 100,
    },
  ],
  [
    SCRAPER_NAME.TiktokCommentByKeywordForSyncly,
    {
      source: SOURCE_TYPE.TIKTOK,
      category: SCRAPER_CATEGORY.SOCIAL,
      fields: [...KeywordDefinitionFieldList, ...SocialMediaFieldList],
      maxItems: 30000,
    },
  ],
  [
    SCRAPER_NAME.TiktokCommentByProfile,
    {
      source: SOURCE_TYPE.TIKTOK,
      category: SCRAPER_CATEGORY.SOCIAL,
      fields: [SCRAPER_REQ_FIELD_KEY.USERNAME, ...SocialMediaFieldList],
      maxItems: 30000,
    },
  ],
  [
    SCRAPER_NAME.NaverStoreReviewByUrl,
    {
      source: SOURCE_TYPE.NAVER_STORE,
      category: SCRAPER_CATEGORY.COMMERCE,
      fields: [...CommerceReviewFieldList],
      maxItems: 1000,
    },
  ],
  [
    SCRAPER_NAME.GoogleMapReviewByUrl,
    {
      source: SOURCE_TYPE.GOOGLE_MAP_REVIEW,
      category: SCRAPER_CATEGORY.COMMUNITY,
      fields: [SCRAPER_REQ_FIELD_KEY.LINK],
      maxItems: 30000,
    },
  ],
  [
    SCRAPER_NAME.ThriveMarketReview,
    {
      source: SOURCE_TYPE.THRIVE_MARKET,
      category: SCRAPER_CATEGORY.COMMERCE,
      fields: [...CommerceReviewFieldList],
      maxItems: 10000,
    },
  ],
  [
    SCRAPER_NAME.LazadaReview,
    {
      source: SOURCE_TYPE.LAZADA,
      category: SCRAPER_CATEGORY.COMMERCE,
      fields: [...CommerceReviewFieldList],
      maxItems: 30000,
    },
  ],
  [
    SCRAPER_NAME.ShopeeReview,
    {
      source: SOURCE_TYPE.SHOPEE,
      category: SCRAPER_CATEGORY.COMMERCE,
      fields: [...CommerceReviewFieldList],
      maxItems: 30000,
    },
  ],
  [
    SCRAPER_NAME.InstagramReelsByKeywordForSyncly,
    {
      source: SOURCE_TYPE.INSTAGRAM_REELS,
      category: SCRAPER_CATEGORY.SOCIAL,
      fields: [...KeywordDefinitionFieldList, ...SocialMediaFieldList],
      maxItems: 100,
    },
  ],
  [
    SCRAPER_NAME.YoutubeShortsByKeywordForSyncly,
    {
      source: SOURCE_TYPE.YOUTUBE,
      category: SCRAPER_CATEGORY.SOCIAL,
      fields: [...KeywordDefinitionFieldList, ...SocialMediaFieldList],
      maxItems: 100,
    },
  ],
  [
    SCRAPER_NAME.TiktokPostByProfile,
    {
      source: SOURCE_TYPE.TIKTOK,
      category: SCRAPER_CATEGORY.SOCIAL,
      fields: [SCRAPER_REQ_FIELD_KEY.USERNAME, ...SocialMediaFieldList],
      maxItems: 100,
    },
  ],
  [
    SCRAPER_NAME.TwitterPostByKeyword,
    {
      source: SOURCE_TYPE.TWITTER,
      category: SCRAPER_CATEGORY.SOCIAL,
      fields: [...KeywordDefinitionFieldList, ...SocialMediaFieldList],
      maxItems: 100,
      customFieldConfig: new Map([
        [
          SCRAPER_REQ_FIELD_KEY.DEFINITION,
          {
            required: true,
          },
        ],
      ]),
    },
  ],
]);

export const SourceScraperMap = new ConstMap(
  Array.from(scraperConfigMap.entries()).reduce(
    (acc, [scraperName, scraperConfig]) => {
      const source = scraperConfig.source;
      const prev = acc.get(source);
      if (prev) {
        acc.set(source, [...prev, scraperName]);
      } else {
        acc.set(source, [scraperName]);
      }
      return acc;
    },
    new Map<SOURCE_TYPE, SCRAPER_NAME[]>([]),
  ),
);
